/**
 * What a call cost, estimated from what the provider told us it consumed.
 *
 * ## Estimated, and saying so
 *
 * OpenAI does not issue a receipt per request. What comes back is a token count, and the invoice
 * arrives monthly in aggregate. So every figure this module produces is an *estimate*: token counts
 * from the response multiplied by a price table checked by hand on {@link PRICES_CHECKED}. The
 * column it lands in is called `estimated_cost_usd` for the same reason `photo_variants` already
 * has one — the honest name is the one that stops someone treating it as an invoice.
 *
 * It is accurate enough for the job it has: knowing which features and which accounts spend the
 * money, catching a runaway before it becomes a bill, and setting a credit price with a real margin
 * rather than a guess. Reconciliation against the provider's dashboard is monthly and manual, and
 * the point of `PRICES_CHECKED` is that a stale table announces itself instead of quietly drifting.
 *
 * ## An unpriced model costs `null`, not zero
 *
 * A model with no entry here returns null and the ledger stores NULL. That is deliberate: a
 * fabricated number would corrupt exactly the reconciliation this file exists to support, and a
 * zero would be indistinguishable from a free call. The models that carry a default are all priced
 * — there is a test for that — so a null means someone typed a model into the "Other" box, which is
 * a thing worth being able to see in the ledger rather than a thing worth guessing about.
 *
 * Like the rest of `shared/`, this compiles under both tsconfigs and imports nothing.
 */

/**
 * When the tables below were last checked against the provider's published pricing.
 *
 * Not decoration. Every number here is a hand-copied fact with a shelf life, and a cost report is
 * worse than no report when it is confidently six months out of date. Re-check on the monthly
 * reconciliation and move this date whether or not anything changed.
 */
export const PRICES_CHECKED = '2026-08-28';

/** USD per million tokens. */
export interface TokenPrices {
  input: number;
  /** Cached input, which on a large stable prompt prefix is where most of the saving lives. */
  cachedInput: number;
  output: number;
}

/**
 * Per-model token pricing, USD per million tokens.
 *
 * Only the models the app actually defaults to or offers in a dropdown are priced. The older
 * entries in the allow-list exist so a key without GPT-5.6 access still works; nobody is routed to
 * them, and inventing prices for them would put fiction in the ledger.
 */
export const MODEL_TOKEN_PRICES: Record<string, TokenPrices> = {
  'gpt-5.6-luna': { input: 0.2, cachedInput: 0.02, output: 1.2 },
  'gpt-5.6-terra': { input: 2, cachedInput: 0.2, output: 12 },
  'gpt-5.6-sol': { input: 4, cachedInput: 0.4, output: 20 },
};

/** What a `/v1/responses` call reported consuming. Cached tokens are a subset of the input. */
export interface TokenUsage {
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
}

/**
 * Estimated USD for one Responses call, or null when the model is unpriced.
 *
 * `cachedTokens` is subtracted from `inputTokens` rather than added alongside, because that is how
 * the provider reports it: `input_tokens` is the total and `input_tokens_details.cached_tokens` is
 * the discounted portion of it. Adding them would double-count the cheapest half of a cached call
 * and make prompt caching look like it increased costs.
 */
export function estimateTokenCostUsd(model: string, usage: TokenUsage): number | null {
  const prices = MODEL_TOKEN_PRICES[model];
  if (prices === undefined) return null;

  const cached = Math.max(0, Math.min(usage.cachedTokens, usage.inputTokens));
  const fresh = Math.max(0, usage.inputTokens - cached);
  const usd =
    (fresh * prices.input + cached * prices.cachedInput + usage.outputTokens * prices.output) /
    1_000_000;
  return round6(usd);
}

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

export type ImageQuality = 'low' | 'medium' | 'high';

export interface ImagePixelSize {
  width: number;
  height: number;
}

/**
 * Published per-image `gpt-image-2` pricing (USD), by quality tier and size.
 *
 * Moved here from `src/capture/ImageSpec.ts`, which still re-exports the estimator it used to own —
 * the pre-flight warning in the enhance panel and the ledger row are now the same arithmetic, which
 * is the only way the number a user was shown before a render can be trusted to match the one
 * recorded after it.
 *
 * Sizes not listed are estimated from the nearest listed size scaled by pixel count.
 */
const IMAGE_PRICES: Record<ImageQuality, { size: ImagePixelSize; usd: number }[]> = {
  low: [
    { size: { width: 1024, height: 1024 }, usd: 0.006 },
    { size: { width: 1536, height: 1024 }, usd: 0.005 },
    { size: { width: 1024, height: 1536 }, usd: 0.005 },
  ],
  medium: [
    { size: { width: 1024, height: 1024 }, usd: 0.053 },
    { size: { width: 1536, height: 1024 }, usd: 0.041 },
    { size: { width: 1024, height: 1536 }, usd: 0.041 },
  ],
  high: [
    { size: { width: 1024, height: 1024 }, usd: 0.211 },
    { size: { width: 1536, height: 1024 }, usd: 0.165 },
    { size: { width: 1024, height: 1536 }, usd: 0.165 },
  ],
};

/** The image models this table covers. `gpt-image-1` is unpriced — see the module comment. */
const PRICED_IMAGE_MODELS: readonly string[] = ['gpt-image-2'];

/**
 * Estimated USD for an image request. Advisory: the provider is the authority on billing.
 *
 * Exported without a model parameter because that is the shape the UI has always used, and the UI
 * only ever renders on `gpt-image-2`. The ledger goes through {@link estimateImageCallCostUsd},
 * which does check the model.
 */
export function estimateImageCostUsd(
  size: ImagePixelSize,
  quality: ImageQuality,
  count = 1,
): number {
  const rows = IMAGE_PRICES[quality];
  const pixels = size.width * size.height;
  const exact = rows.find((row) => row.size.width === size.width && row.size.height === size.height);
  if (exact) return round4(exact.usd * count);

  let nearest = rows[0] as { size: ImagePixelSize; usd: number };
  let bestDelta = Infinity;
  for (const row of rows) {
    const delta = Math.abs(row.size.width * row.size.height - pixels);
    if (delta < bestDelta) {
      bestDelta = delta;
      nearest = row;
    }
  }
  const nearestPixels = nearest.size.width * nearest.size.height;
  return round4(((nearest.usd * pixels) / nearestPixels) * count);
}

/**
 * Estimated USD for one image call as the server saw it, or null when it cannot be priced.
 *
 * The server reads `size`, `quality` and `n` off the request body, which means it gets whatever the
 * client sent — and the swatch call sends no quality at all, leaving the provider to pick. Absent
 * or `auto` is priced as **medium**, which is a stated assumption rather than a measurement: it is
 * the middle of a 35× spread, it is what the provider's own default resolves to in practice, and
 * declining to price the single most common image call in the app would leave a hole in exactly the
 * number this phase exists to produce. The monthly reconciliation is what corrects it.
 */
export function estimateImageCallCostUsd(
  model: string,
  size: string | null,
  quality: string | null,
  count: number,
): number | null {
  if (!PRICED_IMAGE_MODELS.includes(model)) return null;
  const parsed = parseImageSize(size);
  if (parsed === null) return null;
  return estimateImageCostUsd(parsed, normaliseQuality(quality), Math.max(1, count));
}

/** `"1536x1024"` → `{ width, height }`. Null for anything that is not that shape. */
export function parseImageSize(size: string | null): ImagePixelSize | null {
  if (size === null) return null;
  const match = /^(\d{2,5})x(\d{2,5})$/.exec(size.trim());
  if (!match) return null;
  return { width: Number(match[1]), height: Number(match[2]) };
}

/** See {@link estimateImageCallCostUsd} on why an unstated quality is priced as medium. */
export function normaliseQuality(quality: string | null): ImageQuality {
  if (quality === 'low' || quality === 'medium' || quality === 'high') return quality;
  return 'medium';
}

function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/** Six places, matching `ai_usage.estimated_cost_usd` — a Luna call is worth a fraction of a cent. */
function round6(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}
